import React, { useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { format } from 'date-fns';
import Cookies from "js-cookie";
import API_BASE_URL from '../../ApiConfig/ApiConfig';

const SchemeStatus = () => {
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalScheme, setModalScheme] = useState(null);
  const token = Cookies.get("token");
  const [rejectionReason, setRejectionReason] = useState(null);

  const fetchSchemes = async (customerId) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/Customer-scheme/customer-scheme/${customerId}`, {
        headers: {
          "Content-Type": "application/json",
          "Authorization": token ? `Bearer ${token}` : "",
        }
      });
      const data = await response.json();

      if (response.ok) {
        const groupedSchemes = data.reduce((acc, scheme) => {
          const key = `${scheme.schemeId}-${scheme.schemeName}`;
          if (!acc[key]) {
            acc[key] = { ...scheme, tickets: [] };
          }
          acc[key].tickets.push({
            ticketNo: scheme.ticketNo,
            date: scheme.schemeEndDate,
            status: scheme.status,
            invoiceFile: scheme.invoiceFile,
            purchaseDate: scheme.purchaseDate,
            purchaseFrom: scheme.purchaseFrom,
            invoiceAmount: scheme.invoiceAmount,
            rejectionReason: scheme.rejectionReason, // Ensure this is passed from the API
          });
          return acc;
        }, {});

        setSchemes(Object.values(groupedSchemes));
      }
    } catch (err) {
      console.error('Error fetching schemes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = Cookies.get('token');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        const customerId = decodedToken.customerId;
        if (customerId) {
          fetchSchemes(customerId);
        } else {
          console.error('Customer ID not found in token');
        }
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    } else {
      console.error('No token found');
    }
  }, []);

  return (
    <div className="min-w-full mx-auto mt-10 md:mt-20 bg-white rounded-2xl shadow-md">
      <div className="p-4">
        <h1 className="text-xl md:text-2xl font-bold text-gray-700 mb-2">Explore Our Schemes</h1>
      </div>

      <div className="overflow-x-auto">
        {schemes.length === 0 ? (
          <p className="text-center text-gray-500 my-10">No schemes available.</p>
        ) : (
          <>
            {/* Desktop Table */}
            <table className="w-full bg-white rounded-md border-collapse hidden md:table">
              <thead className="bg-[#1C50A3] text-[#FFFFFF]">
                <tr className="border-b border-gray-300">
                  <th className="py-3 px-6 font-semibold text-left">Scheme Name</th>
                  <th className="py-3 px-6 font-semibold text-left">Scheme ID</th>
                  <th className="py-3 px-6 font-semibold text-left">Date</th>
                  <th className="py-3 px-6 font-semibold text-left">Action</th>
                </tr>
              </thead>

              <tbody>
                {schemes.map((scheme) => (
                  <tr key={scheme.schemeId} className="hover:bg-gray-100 transition border-b border-gray-300">
                    <td className="py-3 px-6 text-gray-700 text-[14px] font-medium">
                      {scheme.schemeName}
                    </td>
                    <td className="py-3 px-6 text-gray-700 text-[14px] font-medium">
                      ID No. {scheme.schemeId}
                    </td>
                    <td className="py-3 px-6 text-left">
                      <span className="px-3 py-1 rounded-full text-[14px] font-medium bg-blue-100 text-blue-600">
                        {new Date(scheme.schemeEndDate).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                    </td>
                    <td className="py-3 px-6 text-left">
                      <button
                        className="text-[#1C50A3] font-bold transition"
                        onClick={() => setModalScheme(scheme)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Table */}
            <div className="md:hidden">
              {schemes.map((scheme) => (
                <div key={scheme.schemeId} className="p-4 border-b hover:bg-gray-100 transition">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 font-semibold">Scheme Name</span>
                    <span className="text-gray-700">{scheme.schemeName}</span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-gray-700 font-semibold">Scheme ID</span>
                    <span className="text-gray-700">ID No. {scheme.schemeId}</span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-gray-700 font-semibold">Status</span>
                    <span className="px-3 py-1 rounded-full text-[14px] font-medium bg-blue-100 text-blue-600">
                      {scheme.tickets[0]?.status || "N/A"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-gray-700 font-semibold">Action</span>
                    <button
                      className="text-[#1C50A3] font-bold"
                      onClick={() => setModalScheme(scheme)}
                    >
                      View
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {modalScheme && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-[9999]">
          <div className="bg-white rounded-2xl shadow-2xl p-4 md:p-8 w-full max-w-screen-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-4 md:mb-6 border-b pb-2">
              {modalScheme.schemeName} - {modalScheme.schemeId}
            </h2>

            {/* Ticket Details */}
            <div className="overflow-x-auto">
              {/* Desktop Table */}
              <table className="w-full border-collapse hidden md:table">
                <thead className="bg-gray-300 text-gray-700">
                  <tr>
                    <th className="py-3 px-5 text-left">Ticket No</th>
                    <th className="py-3 px-5 text-left">Date</th>
                    <th className="py-3 px-5 text-left">Status</th>
                    <th className="py-3 px-5 text-left">Purchase Date</th>
                    <th className="py-3 px-5 text-left">Purchase From</th>
                    <th className="py-3 px-5 text-left">Invoice Amount</th>
                    <th className="py-3 px-5 text-left">Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {modalScheme.tickets.map((ticket, index) => (
                    <tr
                      key={index}
                      className={`border-t ${index % 2 === 0 ? "bg-gray-100" : "bg-white"}`}
                    >
                      <td className="py-3 px-5">{ticket.ticketNo}</td>
                      <td className="py-3 px-5">
                        {new Date(ticket.date).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </td>
                      <td className="py-3 px-5">
                        <span
                          className={`font-semibold cursor-pointer ${
                            ticket.status === "Rejected" ? "text-red-500" :
                            ticket.status === "In Process" ? "text-yellow-500" :
                            ticket.status === "Wait for Confirmation" ? "text-yellow-500" :
                            ticket.status === "Approved" ? "text-green-500" : "text-gray-700"
                          } opacity-90`}
                          onClick={() => {
                            if (ticket.status === "Rejected") {
                              setRejectionReason(ticket.rejectionReason);
                            }
                          }}
                        >
                          {ticket.status}
                        </span>
                      </td>
                      <td className="py-3 px-5">
                        {new Date(ticket.purchaseDate).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </td>
                      <td className="py-3 px-5">{ticket.purchaseFrom}</td>
                      <td className="py-3 px-5">₹{ticket.invoiceAmount}</td>
                      <td className="py-3 px-5">
                        <a
                          href={`${API_BASE_URL}${ticket.invoiceFile}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#1C50A3] font-bold underline"
                        >
                          View Invoice
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile Table */}
              <div className="md:hidden">
                {modalScheme.tickets.map((ticket, index) => (
                  <div
                    key={index}
                    className="p-4 border-b hover:bg-gray-100 transition"
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-semibold">Ticket No</span>
                      <span className="text-gray-700">{ticket.ticketNo}</span>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-gray-700 font-semibold">Date</span>
                      <span className="text-gray-700">
                        {new Date(ticket.date).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-gray-700 font-semibold">Status</span>
                      <span
                        className={`font-semibold ${ticket.status === "Rejected"
                          ? "text-red-500"
                          : ticket.status === "In Process"
                            ? "text-yellow-500"
                            : ticket.status === "Wait for Confirmation"
                              ? "text-yellow-500"
                              : ticket.status === "Approved"
                                ? "text-green-500"
                                : "text-gray-700"
                          } opacity-90`}
                      >
                        {ticket.status}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-gray-700 font-semibold">Purchase Date</span>
                      <span className="text-gray-700">
                        {new Date(ticket.purchaseDate).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-gray-700 font-semibold">Purchase From</span>
                      <span className="text-gray-700">{ticket.purchaseFrom}</span>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-gray-700 font-semibold">Invoice Amount</span>
                      <span className="text-gray-700">₹{ticket.invoiceAmount}</span>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-gray-700 font-semibold">Invoice</span>
                      <a
                        href={`${API_BASE_URL}${ticket.invoiceFile}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#1C50A3] font-bold underline"
                      >
                        View Invoice
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Close Button */}
            <div className="mt-6 flex justify-end">
              <button
                className="px-4 py-2 bg-red-500 text-white rounded-[4px] hover:bg-red-600 transition"
                onClick={() => setModalScheme(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {rejectionReason && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-[9999]">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md">
            <h2 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">
              Rejection Reason
            </h2>
            <p className="text-gray-700">{rejectionReason}</p>
            <div className="mt-4 flex justify-end">
              <button
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition"
                onClick={() => setRejectionReason(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchemeStatus;